import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { projectIntroApi } from '../api/endpoints';
import { usePortalBranding } from '../context/PortalContext';
import { resolveApiAssetUrl } from '../api/client';
import { PortalLogo } from '../components/PortalLogo';
import { loginLogoHeight } from '../components/portalLogoSize';
import type { IntroPageType } from '../types';

interface ProjectIntroPublicProps {
  type: IntroPageType;
}

/**
 * Gọi projectIntroApi.getPublic nhưng không bao giờ throw — lỗi khác 404
 * (500, mất mạng) cũng phải về null để UI hiện "chưa sẵn sàng" thay vì treo
 * spinner vô hạn + unhandled rejection.
 */
export async function loadIntroPageSafely(
  projectId: string,
  type: IntroPageType,
): Promise<{ title: string; content_html: string } | null> {
  try {
    return await projectIntroApi.getPublic(projectId, type);
  } catch {
    return null;
  }
}

/**
 * Trang công khai (không cần đăng nhập) hiện nội dung đã publish của 1 dự án.
 * Dùng chung cho /gioi-thieu/:projectId (type="demo") và /huong-dan/:projectId
 * (type="guide") — xem App.tsx.
 */
export function ProjectIntroPublic({ type }: ProjectIntroPublicProps): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const { branding } = usePortalBranding();
  const logoSrc = resolveApiAssetUrl(branding?.logo_url);
  const [page, setPage] = useState<{ title: string; content_html: string } | null | undefined>(undefined);

  useEffect(() => {
    if (!projectId) return;
    let active = true;
    void loadIntroPageSafely(projectId, type).then((result) => {
      if (active) setPage(result);
    });
    return () => {
      active = false;
    };
  }, [projectId, type]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Paper elevation={2} sx={{ maxWidth: 720, mx: 'auto', borderRadius: 3, overflow: 'hidden' }}>
        {logoSrc && (
          <PortalLogo
            src={logoSrc}
            alt={branding?.display_name ?? ''}
            height={loginLogoHeight(branding?.logo_height)}
            onDark={Boolean(branding?.logo_on_dark)}
          />
        )}
        <Box sx={{ p: 4 }}>
          {page === undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {page === null && (
            <Typography color="text.secondary" textAlign="center">
              Trang chưa sẵn sàng, vui lòng liên hệ bên cung cấp dịch vụ.
            </Typography>
          )}
          {page && (
            <>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                {page.title}
              </Typography>
              {/* content_html chỉ do Admin (đã qua requireAdmin phía API) ghi được — không có
                  đường nào cho input công khai chạm tới trường này. */}
              <Box dangerouslySetInnerHTML={{ __html: page.content_html }} />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
